/** Cliente do stream de eventos (SPEC-FE-001 § 3).

 * `fetch` com `ReadableStream`, e não `EventSource`: o nativo não manda
 * cabeçalho, e a única saída seria o access token na URL — que vai para o log
 * do nginx, para o histórico e para o `Referer`. O preço é a reconexão à mão,
 * que é este arquivo.
 *
 * **A reconexão não é opcional.** O servidor fecha o stream de propósito
 * quando o token que o autenticou vence, e todo trânsito de rede — troca de
 * Wi-Fi, suspensão do notebook, blue/green trocando de slot — encerra a
 * conexão. Sem reconectar, o tempo real dura quinze minutos e some sem avisar
 * ninguém.
 *
 * **Com recuo exponencial e teto.** Um servidor caindo com dez abas abertas
 * tentando reconectar a cada segundo é o cliente ajudando a derrubar o
 * servidor.
 */

import { http } from "./http";

/** Primeira espera. Curta: a queda mais comum é a troca de slot do deploy, que
 * dura segundos. */
export const RECUO_INICIAL_MS = 1_000;

/** Teto do recuo. Trinta segundos: além disso, quem voltou ao escritório
 * esperaria demais por um board que já podia estar em dia. */
export const RECUO_MAXIMO_MS = 30_000;

export interface Mudanca {
  projecao: string;
  event_type: string;
  aggregate_id: string;
}

export interface Assinatura {
  /** Encerra a conexão e desliga a reconexão. */
  fechar: () => void;
}

export interface OpcoesDoStream {
  /** Chamado a cada aviso de mudança. */
  aoMudar: (mudanca: Mudanca) => void;
  /** Chamado quando o stream conecta e quando cai. É o que a tela usa para
   * decidir se ainda precisa reler por tempo. */
  aoConectar?: (conectado: boolean) => void;
  /** Injetável para o teste não esperar segundos de verdade. */
  esperar?: (ms: number, signal: AbortSignal) => Promise<void>;
}

export function recuoDe(tentativa: number): number {
  return Math.min(RECUO_INICIAL_MS * 2 ** tentativa, RECUO_MAXIMO_MS);
}

/** Quebra o texto acumulado em quadros SSE completos.
 *
 * Exportada porque é onde mora a única sutileza do protocolo: um quadro só
 * está inteiro quando chega a linha em branco, e o `ReadableStream` corta onde
 * quiser — inclusive no meio de um `data:`. Tratar cada pedaço como um quadro
 * funciona no teste e falha em produção, com o JSON pela metade.
 */
export function quadrosCompletos(texto: string): { quadros: string[]; resto: string } {
  const partes = texto.split("\n\n");
  return { quadros: partes.slice(0, -1), resto: partes[partes.length - 1] ?? "" };
}

/** Lê o `data:` de um quadro. `null` para o que não interessa — batimento é
 * comentário (`: batimento`), e o evento `pronto` não tem conteúdo. */
export function mudancaDoQuadro(quadro: string): Mudanca | null {
  const linhas = quadro.split("\n");
  const evento = linhas
    .find((linha) => linha.startsWith("event:"))
    ?.slice(6)
    .trim();
  if (evento !== "mudanca") return null;

  const dados = linhas
    .find((linha) => linha.startsWith("data:"))
    ?.slice(5)
    .trim();
  if (dados === undefined) return null;

  try {
    return JSON.parse(dados) as Mudanca;
  } catch {
    // Quadro ilegível não derruba o stream: o próximo evento chega igual.
    return null;
  }
}

/** Espera que **acaba junto com a assinatura**.
 *
 * Um `setTimeout` solto sobrevive a quem o criou: fechar a tela abortava o
 * `fetch` e deixava o temporizador da próxima tentativa correndo até trinta
 * segundos depois, em cima de um componente que já não existe. No CI isso
 * apareceu como `ReferenceError: window is not defined` **depois** do fim do
 * teste — o ambiente já tinha sido desmontado quando o relógio disparou.
 */
const dormir = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolver) => {
    const relogio = setTimeout(resolver, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(relogio);
        resolver();
      },
      { once: true },
    );
  });

export function ouvirEventos({
  aoMudar,
  aoConectar,
  esperar = dormir,
}: OpcoesDoStream): Assinatura {
  // Um estado só, e é o `AbortController`: um booleano paralelo diria "vivo"
  // enquanto o `fetch` já foi cancelado, e as duas versões da verdade
  // divergiriam exatamente na hora de desmontar a tela.
  const controle = new AbortController();
  // O aviso de conexão para de sair no instante do `fechar`, e não quando o
  // laço reparar no `abort`. Entre uma coisa e outra cabe um `setState` num
  // componente já desmontado — no CI isso apareceu como
  // `ReferenceError: window is not defined` **depois** do fim do teste, com os
  // 290 verdes e o processo saindo com erro.
  let avisar = aoConectar;

  async function laco(): Promise<void> {
    let tentativa = 0;

    while (!controle.signal.aborted) {
      try {
        const resposta = await http.abrirStream("/events/stream", controle.signal);
        if (!resposta.ok || resposta.body === null) throw new Error(String(resposta.status));

        // Conectou: zera o recuo. Sem isto, uma queda depois de horas de
        // conexão saudável já começaria esperando trinta segundos.
        tentativa = 0;
        avisar?.(true);

        const leitor = resposta.body.pipeThrough(new TextDecoderStream()).getReader();
        let pendente = "";
        for (;;) {
          const { value, done } = await leitor.read();
          if (done) break;
          const { quadros, resto } = quadrosCompletos(pendente + value);
          pendente = resto;
          for (const quadro of quadros) {
            const mudanca = mudancaDoQuadro(quadro);
            if (mudanca !== null) aoMudar(mudanca);
          }
        }
      } catch {
        // Toda falha cai aqui igual: rede, 401 que a renovação não salvou,
        // servidor fora. A diferença entre elas não muda o que fazer.
      }

      avisar?.(false);
      // Sem checar `aborted` de novo: quem reavalia é o `while` no topo, e
      // repetir a condição aqui é a linha que o lint recusa — com razão, ela
      // dá a impressão de haver duas saídas quando só existe uma.
      await esperar(recuoDe(tentativa), controle.signal);
      tentativa += 1;
    }
  }

  void laco();

  return {
    fechar: () => {
      avisar = undefined;
      controle.abort();
    },
  };
}
